/* eslint-disable max-lines */

import type {SharedData} from '@gaia/types';
import concatMapboxStyles from '@gaia/utils/mapbox/concatMapboxStyles';
import compact from 'lodash/compact';
import fromPairs from 'lodash/fromPairs';
import map from 'lodash/map';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import uniqBy from 'lodash/uniqBy';
import values from 'lodash/values';

import {defaultPinLayer} from './constants';
import {waypointFixedLayerIds} from './fixedLayerIds';

interface GetWaypointsMapboxStyleOptions {
    sharedData: SharedData;
    selections: SelectionState[];
    interaction: InteractionState;
    isPrinting: boolean;
    showWaypointLabels: undefined | boolean;
    showWaypoints: boolean;
    waypoints: {[id: string]: Waypoint};
    tracks: {[id: string]: Track};
    isAuthenticated: boolean;
    userSource: mapboxgl.VectorSource | undefined;
    visibilities: Record<string, boolean>;
    showArchived: boolean;
    activeFolderIds: string[] | undefined;
}

type LocalFixedLayerIds = (typeof waypointFixedLayerIds)['localUser'];
type RemoteFixedLayerIds = (typeof waypointFixedLayerIds)['remoteUser'];

export default function getStyle(options: GetWaypointsMapboxStyleOptions) {
    return {
        metadata: {
            id: 'waypoints',
        },
        ...concatMapboxStyles(
            compact([
                getRemoteUserStyle(waypointFixedLayerIds.remoteUser, options),
                getLocalUserStyle(waypointFixedLayerIds.localUser, options),
                getRemoteSharedStyle(waypointFixedLayerIds.remoteShared, options),
                getLocalSharedStyle(waypointFixedLayerIds.localShared, options),
            ])
        ),
        glyphs: 'https://static.gaiagps.com/GaiaTopoGL/glyphs/{fontstack}/{range}.pbf',
    };
}

function getLocalUserStyle(
    layerIds: LocalFixedLayerIds,
    {
        interaction,
        showWaypointLabels,
        showWaypoints,
        waypoints,
        sharedData,
        selections,
        isPrinting,
        visibilities,
        tracks,
        showArchived,
        activeFolderIds,
    }: GetWaypointsMapboxStyleOptions
): mapboxgl.Style | undefined {
    let features = values(waypoints).filter(
        (waypoint: Waypoint) => !isSharedData(sharedData, waypoint, tracks)
    );

    if (interaction.type === 'editingRoute' && interaction.track.isGeometryLoaded) {
        const routeWaypoints = Object.values(interaction.track.points).filter(
            (waypoint) => waypoint.properties.attr === null
        );

        features.unshift(...routeWaypoints);
        features = uniqBy(features, (waypoint: Waypoint) => waypoint.properties.localId);
    }

    if (!features.length) return;

    const filter = getLocalFilter(
        waypoints,
        visibilities,
        interaction,
        selections,
        sharedData,
        showArchived,
        showWaypoints,
        activeFolderIds
    );

    return {
        version: 8,
        metadata: {
            id: 'localUser',
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features,
                },
            },
        },
        layers: [
            getPinLayer({
                id: layerIds.pin,
                interaction,
                showWaypointLabels,
                selections,
                isPrinting,
                visibilities,
                filter,
                activeFolderIds,
            }),
        ],
    };
}

function isSharedData(sharedData: SharedData, waypoint: Waypoint, tracks: Record<string, Track>) {
    if (!sharedData) {
        return false;
    } else if (sharedData.type === 'waypoint') {
        return sharedData.id === waypoint.properties.localId;
    } else if (sharedData.type === 'track') {
        return (
            sharedData.id === waypoint.properties.trackLocalId ||
            sharedData.id === waypoint.properties.track_id
        );
    } else if (sharedData.type === 'folder') {
        const track = waypoint.properties.track_id && tracks[waypoint.properties.track_id];
        return (
            waypoint.properties.folder === sharedData.id ||
            (track && track.properties.folder === sharedData.id)
        );
    } else {
        return false;
    }
}

function getLocalSharedStyle(
    layerIds: LocalFixedLayerIds,
    {
        interaction,
        showWaypointLabels,
        waypoints,
        sharedData,
        tracks,
        selections,
        isPrinting,
        showWaypoints,
        visibilities,
        showArchived,
        activeFolderIds,
    }: GetWaypointsMapboxStyleOptions
): mapboxgl.Style | undefined {
    const features = values(waypoints).filter((waypoint: Waypoint) =>
        isSharedData(sharedData, waypoint, tracks)
    );

    if (!features.length) return;

    const filter = getLocalFilter(
        waypoints,
        visibilities,
        interaction,
        selections,
        sharedData,
        showArchived,
        showWaypoints,
        activeFolderIds
    );

    return {
        version: 8,
        metadata: {
            id: 'localShared',
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features,
                },
            },
        },
        layers: [
            getPinLayer({
                id: layerIds.pin,
                interaction,
                showWaypointLabels,
                selections,
                visibilities,
                isPrinting,
                filter,
                activeFolderIds,
            }),
        ],
    };
}

function getRemoteSharedStyle(
    layerIds: RemoteFixedLayerIds,
    {
        interaction,
        sharedData,
        showWaypointLabels,
        waypoints,
        selections,
        isPrinting,
        showWaypoints,
        visibilities,
        showArchived,
        activeFolderIds,
    }: GetWaypointsMapboxStyleOptions
): mapboxgl.Style | undefined {
    const source = getRemoteSharedSource(sharedData);
    if (!source) return;

    const filter = getRemoteFilter(
        waypoints,
        {},
        interaction,
        selections,
        sharedData,
        showArchived,
        showWaypoints,
        activeFolderIds
    );

    return {
        version: 8,
        metadata: {
            id: 'remoteShared',
        },
        sources: {source},
        layers: [
            getPinLayer({
                id: layerIds.pin,
                interaction,
                showWaypointLabels,
                ...(source.type === 'vector' && {'source-layer': 'waypoints'}),
                filter,
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
        ],
    };
}

function getRemoteSharedSource(sharedData: SharedData): mapboxgl.AnySourceData | undefined {
    if (sharedData && sharedData.type === 'waypoint') {
        return {
            type: 'geojson',
            data: `${window.location.origin}/api/objects/waypoint/${sharedData.id}/?pubLink=${sharedData.pubLink}`,
        };
    } else if (sharedData && sharedData.type === 'folder') {
        return {
            type: 'vector',
            tiles: [
                `${window.location.origin}/api/tiles/folders/${sharedData.id}/{z}/{x}/{y}.mvt?pubLink=${sharedData.pubLink}`,
            ],
        };
    } else if (sharedData && sharedData.type === 'track') {
        return {
            type: 'geojson',
            data: `${window.location.origin}/api/objects/track/${sharedData.id}/?pubLink=${sharedData.pubLink}`,
        };
    }
}

function getRemoteUserStyle(
    layerIds: RemoteFixedLayerIds,
    {
        interaction,
        showWaypoints,
        waypoints,
        showWaypointLabels,
        sharedData,
        isAuthenticated,
        selections,
        isPrinting,
        visibilities,
        showArchived,
        userSource,
        activeFolderIds,
    }: GetWaypointsMapboxStyleOptions
): mapboxgl.Style | undefined {
    if (!isAuthenticated || !userSource) return;
    if (!showWaypoints) return;

    const filter = [
        'all',
        getRemoteFilter(
            waypoints,
            visibilities,
            interaction,
            selections,
            sharedData,
            showArchived,
            showWaypoints,
            activeFolderIds
        ),
        sharedData && sharedData.type === 'waypoint'
            ? ['!=', ['get', 'id'], sharedData.id]
            : ['literal', true],
    ];

    return {
        version: 8,
        metadata: {
            id: 'remoteUser',
        },
        sources: {
            source: userSource,
        },
        layers: [
            getPinLayer({
                id: layerIds.pin,
                interaction,
                'source-layer': 'waypoints',
                filter,
                showWaypointLabels,
                selections,
                isPrinting,
                visibilities,
                activeFolderIds,
            }),
        ],
    };
}

interface GetPinLayerOptions extends Partial<mapboxgl.SymbolLayer> {
    interaction: InteractionState;
    showWaypointLabels?: boolean;
    id: string;
    selections: SelectionState[];
    isPrinting: boolean;
    visibilities: Record<string, boolean>;
    activeFolderIds: string[] | undefined;
}

function getPinLayer({
    interaction,
    paint,
    layout,
    showWaypointLabels,
    selections,
    isPrinting,
    visibilities,
    activeFolderIds,
    ...layer
}: GetPinLayerOptions): mapboxgl.SymbolLayer {
    return {
        ...defaultPinLayer,
        minzoom: 3,
        paint: {
            ...defaultPinLayer.paint,
            'icon-opacity': getFocusExpression(
                interaction,
                selections,
                getVisibilityExpression(visibilities, 1, 0.6),
                isPrinting ? 0 : 0.2,
                1,
                activeFolderIds
            ),
            'text-opacity': getFocusExpression(
                interaction,
                selections,
                getVisibilityExpression(visibilities, 1, 0.6),
                isPrinting ? 0 : 0.2,
                1,
                activeFolderIds
            ),
            ...paint,
        },
        layout: {
            ...defaultPinLayer.layout,
            'text-field': showWaypointLabels ? '{title}' : '',
            ...layout,
        },
        ...omit(layer, 'type'),
    };
}

// eslint-disable-next-line max-params
function getFocusExpression<T>(
    interaction: InteractionState,
    selections: SelectionState[],
    focused: T | mapboxgl.Expression,
    blurred: T | mapboxgl.Expression,
    normal: T | mapboxgl.Expression,
    activeFolderIds: string[] | undefined
): T | mapboxgl.Expression {
    if (interaction.type === 'editingWaypoint') {
        return [
            'case',
            [
                '==',
                ['coalesce', ['get', 'localId'], ['get', 'id']],
                interaction.waypoint.properties.localId,
            ],
            focused,
            blurred,
        ];
    } else if (interaction.type === 'editingRoute') {
        // Focus route waypoints when their route is being edited
        return [
            'case',
            ['==', ['get', 'trackLocalId'], interaction.track.properties.localId],
            focused,
            blurred,
        ];
    } else if (interaction.type === 'editingArea') {
        return blurred;
    } else if (selections.length) {
        const inner = compact(
            selections.map((selection) => {
                if (selection.type === 'waypoint') {
                    return [
                        '==',
                        ['coalesce', ['get', 'localId'], ['get', 'id']],
                        selection.localId,
                    ];
                } else if (selection.type === 'track') {
                    return ['==', ['get', 'trackLocalId'], selection.localId];
                } else {
                    return undefined;
                }
            })
        );

        if (inner.length) return ['case', ['any', ...inner], focused, blurred];
        else return blurred;
    } else if (activeFolderIds) {
        // Remote items will have a folder property, but items that have been edited locally
        // will have a parent_folder_id that is more current than the original folder from the server.
        return [
            'case',
            [
                'in',
                [
                    'case',
                    ['has', 'parent_folder_id'],
                    ['get', 'parent_folder_id'],
                    ['get', 'folder'],
                ],
                ['literal', activeFolderIds],
            ],
            focused,
            blurred,
        ];
    } else if (interaction.type !== 'normal') {
        return blurred;
    } else {
        return normal;
    }
}

// eslint-disable-next-line max-params
function getRemoteFilter(
    waypoints: Record<string, Waypoint>,
    visibilities: Record<string, boolean>,
    interaction: InteractionState,
    selections: SelectionState[],
    sharedData: SharedData,
    showArchived: boolean,
    showWaypoints: boolean,
    activeFolderIds: string[] | undefined
) {
    return [
        'all',
        getLocalFilter(
            waypoints,
            visibilities,
            interaction,
            selections,
            sharedData,
            showArchived,
            showWaypoints,
            activeFolderIds
        ),
        [
            '!',
            [
                'has',
                ['get', 'id'],
                [
                    'literal',
                    fromPairs(map(waypoints, (waypoint) => [waypoint.properties.id, true])),
                ],
            ],
        ],
    ];
}

// eslint-disable-next-line max-params
function getLocalFilter(
    waypoints: Record<string, Waypoint>,
    visibilities: Record<string, boolean>,
    interaction: InteractionState,
    selections: SelectionState[],
    sharedData: SharedData,
    showArchived: boolean,
    showWaypoints: boolean,
    activeFolderIds: string[] | undefined
) {
    const sharedWaypointId = sharedData?.type === 'waypoint' && sharedData.id;

    const selectedWaypointIds = compact(
        [sharedWaypointId].concat(
            selections.map((selection) => selection.type === 'waypoint' && selection.localId)
        )
    );

    return [
        'all',
        ['!=', ['get', 'attr'], 'route'],
        [
            '!=',
            ['coalesce', ['get', 'archived'], ['get', 'deleted'], false],
            showArchived ? null : true,
        ],
        [
            'case',
            interaction.type === 'editingWaypoint' && !interaction.waypoint?.properties?.track_id,
            [
                '!=',
                ['get', 'id'],
                (interaction.type === 'editingWaypoint' && interaction.waypoint?.properties?.id) ||
                    null,
            ],
            true,
        ],
        // If the waypoints overlay is off we should still show selected waypoints
        [
            'case',
            !showWaypoints && !(sharedData?.type === 'folder'),
            [
                'has',
                ['coalesce', ['get', 'localId'], ['get', 'id']],
                ['literal', fromPairs(selectedWaypointIds.map((id) => [id, true]))],
            ],
            true,
        ],
        getFocusExpression(
            interaction,
            selections,
            getVisibilityExpression(visibilities, true, false),
            getVisibilityExpression(visibilities, true, false),
            getVisibilityExpression(visibilities, true, false),
            activeFolderIds
        ),
        // Sometimes the source is a track "FeatureCollection." Mapbox renders verticies in a
        // "LineString" as though points so we must filter them out.
        ['==', ['geometry-type'], 'Point'],
    ];
}

function getVisibilityExpression<T>(
    visibilities: Record<string, boolean>,
    visible: T | mapboxgl.Expression,
    hidden: T | mapboxgl.Expression
): T | mapboxgl.Expression {
    return [
        'case',
        [
            'all',
            ['has', 'id'],
            [
                'has',
                ['get', 'id'],
                ['literal', pickBy(visibilities, (visibility) => visibility === false)],
            ],
        ],
        hidden,
        visible,
    ];
}
